/**
 * DEV-817/DEV-819 — the Leads admin view's domain types and its form rules.
 *
 * A LEAD is a pre-filled profile for someone we sourced online who is not a user
 * yet; when they log in with the email or phone we hold, their profile is already
 * populated. See docs/superpowers/specs/2026-08-26-dev769-lead-users-design.md
 * for the model and 2026-09-08-dev817-leads-admin-view-design.md for this view.
 *
 * Pure by design — no React, no data layer — so the rules below are unit-testable
 * and the form and the RPC cannot drift apart.
 *
 * ONE SET OF RULES, TWO FORMS. DEV-817 added the edit form; DEV-819 added the
 * manual-create form. They write the same eight facts through two RPCs
 * (`admin_update_lead`, `admin_create_lead_manual`) that refuse the same things,
 * so the validator, the builder and the form-value shape below are deliberately
 * neutral about which write they serve. Naming them `…Edit…` and adding a second
 * `…Create…` copy is how the two would drift.
 */
import { CANDIDATE_EMAIL_PATTERN } from "./staffingTypes";

/** `leads.source` CHECK, in the order the table declares it. */
export const LEAD_SOURCES = ["candidate", "manual", "csv", "outreach"] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

/** One row of `public.leads`, as the admin view reads it. */
export interface AdminLead {
  id: string;
  email: string | null;
  phone: string | null;
  displayName: string;
  locationCity: string | null;
  locationCountry: string | null;
  skills: string[];
  employmentTitle: string | null;
  source: LeadSource;
  sourceCandidateId: string | null;
  candidateIdentityId: string | null;
  createdBy: string | null;
  notes: string | null;
  /** The Privy DID that claimed this lead, or null while it is still unclaimed. */
  claimedBy: string | null;
  claimedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A lead is EITHER unclaimed (still a prefill waiting for its subject to log in,
 * and therefore correctable) OR claimed (the person has a real profile, and the
 * row is the record of what was pre-filled). Almost every affordance in the view
 * branches on this, so it is a named filter rather than a loose boolean.
 */
export type LeadClaimFilter = "all" | "claimed" | "unclaimed";

export interface AdminLeadFilters {
  /** Matches email OR phone OR display_name, case-insensitively. */
  search?: string;
  source?: LeadSource;
  claim?: LeadClaimFilter;
  limit?: number;
  offset?: number;
}

/**
 * The editable facts, and only those. `source`, `source_candidate_id`,
 * `candidate_identity_id`, `created_by`, `claimed_by` and `claimed_at` are
 * absent deliberately: provenance is a record of what happened, and a provenance
 * field an admin can rewrite is a provenance field that means nothing. The RPC
 * enforces the same omission.
 */
export interface LeadUpdateInput {
  displayName: string;
  email: string | null;
  phone: string | null;
  locationCity: string | null;
  locationCountry: string | null;
  employmentTitle: string | null;
  skills: string[];
  notes: string | null;
}

/**
 * DEV-819 — what the manual-create form sends.
 *
 * AN ALIAS, NOT A SECOND INTERFACE. `admin_create_lead_manual` accepts exactly
 * the eight facts `admin_update_lead` accepts, for the same reason: the other
 * columns are provenance, and a caller that can choose its own provenance can
 * label a hand-typed row as vendor-sourced. Declaring an identical shape twice
 * would be the drift this module exists to prevent; the alias keeps each call
 * site reading correctly while there stays one definition.
 *
 * `source` and `created_by` are absent on purpose — the RPC sets `'manual'` and
 * `requesting_user_id()` itself, so a client cannot attribute a lead to another
 * admin.
 */
export type LeadCreateInput = LeadUpdateInput;

/**
 * DEV-820 — the THIRD ingest path: a sourcing spreadsheet, imported in bulk.
 *
 * THE SAME EIGHT FACTS AGAIN, for the third time and for the same reason. A CSV
 * import is `admin_create_lead_manual` in the plural: it accepts what the forms
 * accept and sets `source = 'csv'` and `created_by` itself, so a client cannot
 * label a spreadsheet row as vendor-sourced. The column MAPPING (which
 * spreadsheet header feeds which fact) is a separate concern and lives in
 * `leadCsv.ts`; only the outcome vocabulary belongs here, beside the row shape
 * it describes.
 */
export interface LeadImportRow {
  /**
   * The 1-based physical line of the CSV this row came from — for the report,
   * so an admin can go and look at it.
   *
   * NOT the row's ordinal, and the two genuinely differ: a quoted field may
   * contain newlines, so row 2 can begin on line 4. `parseCsv` is the only
   * thing that knows the mapping, so it is carried rather than recomputed. The
   * RPC never sees this — it indexes what it was handed and the client joins
   * the report back by index.
   */
  line: number;
  input: LeadCreateInput;
}

/**
 * What happened to one imported row. Ten values in three groups, and the groups
 * are the point.
 *
 * A DUPLICATE IS NOT A FAILURE, and this vocabulary exists to make that
 * expressible. `idx_leads_email` and `idx_leads_phone` are unique, so
 * re-importing a list inserts nothing — the correct outcome, not an error. An
 * importer with a boolean per row would report "947 of 1,000 rows failed" for a
 * run that did precisely the right thing, and an admin who sees that once stops
 * trusting the importer. So "we already hold this person" is its own group,
 * reported next to the insertions rather than among the rejections.
 *
 * `existing_user` sits in that same middle group rather than with the
 * rejections: nothing is wrong with the row, the person simply already has an
 * account (and their lead could never be claimed — the claim in
 * supabase/functions/profiles/index.ts only runs at FIRST login). An admin
 * scanning for problems should not spend attention on it.
 */
export type LeadImportOutcome =
  /** A new lead exists. */
  | "inserted"
  /** A lead already holds this contact; `leadId` names it. A no-op, not a failure. */
  | "duplicate_lead"
  /** An earlier row of this same file held this contact. */
  | "duplicate_in_file"
  /** The email belongs to a registered profile, so a lead could never be claimed. */
  | "existing_user"
  /** The contact has opted out. Refused by `leads_zz_reject_suppressed_bi`. */
  | "suppressed"
  /** Neither an email nor a phone — `CONSTRAINT leads_has_contact`. */
  | "no_contact"
  /** Blank `display_name` — a placeholder would beat the claimer's own login name. */
  | "no_name"
  /** Not canonical E.164, and the country code cannot be guessed. */
  | "bad_phone"
  /** Not a plausible email shape, so the lead could never match anyone. */
  | "bad_email"
  /** Anything else; `message` carries the raw refusal. */
  | "error";

/** The three groups the report shows. A duplicate belongs to `alreadyHeld`. */
export type LeadImportGroup = "created" | "alreadyHeld" | "rejected";

/**
 * Which group an outcome belongs to. ONE definition, because the toast, the
 * preview counts and the report all branch on it, and a fourth reading of
 * "is `existing_user` a failure?" is how a duplicate ends up looking like one.
 */
export const LEAD_IMPORT_GROUPS: Record<LeadImportOutcome, LeadImportGroup> = {
  inserted: "created",
  duplicate_lead: "alreadyHeld",
  duplicate_in_file: "alreadyHeld",
  existing_user: "alreadyHeld",
  suppressed: "rejected",
  no_contact: "rejected",
  no_name: "rejected",
  bad_phone: "rejected",
  bad_email: "rejected",
  error: "rejected",
};

/** One row's fate, joined back to the CSV line it came from. */
export interface LeadImportRowResult {
  /** The CSV line number, resolved by the caller from the RPC's row index. */
  line: number;
  outcome: LeadImportOutcome;
  /** The lead created, or the one that already held the contact. */
  leadId: string | null;
  /** The database's own message, written for the admin to read. */
  message: string | null;
}

/**
 * The whole run. `dryRun` is carried so a report cannot be mistaken for a
 * commit: a preview and a commit are the same call and produce the same shape,
 * and the only thing separating "will insert 812" from "inserted 812" is this
 * flag.
 */
export interface LeadImportReport {
  dryRun: boolean;
  rows: LeadImportRowResult[];
  counts: Record<LeadImportGroup, number>;
}

/** Group the per-row results. Derived, never stored — one source of truth. */
export function countLeadImport(rows: LeadImportRowResult[]): Record<LeadImportGroup, number> {
  const counts: Record<LeadImportGroup, number> = { created: 0, alreadyHeld: 0, rejected: 0 };
  for (const row of rows) counts[LEAD_IMPORT_GROUPS[row.outcome]] += 1;
  return counts;
}

/**
 * The most rows one import may carry.
 *
 * NOT UNBOUNDED, and the reason is the mechanism that gives the import its
 * per-row isolation. `admin_import_leads` wraps each row in a
 * `BEGIN … EXCEPTION` block — an implicit savepoint — so a bad row rolls back
 * only itself. Past 64 subtransactions a snapshot is marked suboverflowed,
 * which makes visibility checks a little more expensive for CONCURRENT readers
 * while the import runs. At 5,000 admin-initiated rows that cost is worth
 * paying; unbounded it would not be.
 *
 * NOT 1,000 either, even though the ticket's acceptance criterion is a
 * 1,000-row file: sourcing lists arrive a little over round numbers, and
 * refusing 1,050 rows for no technical reason is a bad trade. The RPC enforces
 * the same number, so a caller that skips the client check is still refused.
 */
export const LEAD_IMPORT_MAX_ROWS = 5000;

/** The lead's originating quest candidate, resolved by `admin_lead_source_candidate`. */
export interface LeadSourceCandidate {
  candidateId: string;
  questId: string | null;
  questTitle: string | null;
  candidateName: string | null;
  candidateStatus: string | null;
}

/**
 * Raw form state, shared by the edit and create forms. `skillsStr` is the raw
 * comma string — see `buildLeadInput` for why it is not split per keystroke.
 */
export interface LeadFormValues {
  displayName: string;
  email: string;
  phone: string;
  locationCity: string;
  locationCountry: string;
  employmentTitle: string;
  skillsStr: string;
  notes: string;
}

/** One key per refusal, so the caller owns the translated message. */
export type LeadFormError =
  | "nameRequired"
  | "contactRequired"
  | "emailInvalid"
  | "phoneNotE164";

/**
 * Canonical E.164, as `leads.phone` actually holds it.
 *
 * NOT `isValidPhoneish` from staffingTypes, and that difference is the point.
 * The candidate form is deliberately permissive because vendors return national,
 * international and punctuated forms, and `quest_candidates.phone` stores
 * whatever arrives. `leads.phone` does not: `leads_normalize_phone`
 * (20260907150001) strips formatting, turns a leading `00` into `+`, then RAISES
 * unless the result matches `^\+[1-9][0-9]{6,14}$`. It refuses to guess a country
 * code on purpose — "guessing the country from a 9-digit string is how you
 * contact a stranger who happens to share a suffix with the person you meant".
 *
 * So this mirrors the trigger rather than the sibling form. Do not relax it to
 * `isValidPhoneish`: that would move the refusal from an inline field message to
 * an opaque Postgres toast.
 */
export const LEAD_E164_PATTERN = /^\+[1-9][0-9]{6,14}$/;

/** The formatting characters `leads_normalize_phone` strips before matching. */
const PHONE_FORMATTING = /[\s\-.()]/g;

/**
 * What `leads_normalize_phone` would store for this input, or `null` if it would
 * refuse. Applies the same two transformations (strip formatting, `00` → `+`)
 * before testing the pattern, so an admin may type `+595 981-123456` or
 * `00595981123456` and be accepted.
 *
 * NOT FOR PRE-NORMALIZING A WRITE. `buildLeadInput` deliberately sends what the
 * admin typed: `leads_normalize_biu` owns normalization for all four ingest
 * paths, and a second copy applied on the way in is the drift 20260907150001
 * exists to prevent. This exists for the two callers that must PREDICT the
 * trigger without being it — the form's inline validation, and
 * `MockStaffingService`, which stands in for the database in mock mode and has
 * no trigger to do it.
 */
export function normalizeLeadPhone(value: string): string | null {
  const stripped = value.trim().replace(PHONE_FORMATTING, "");
  const normalized = stripped.startsWith("00") ? `+${stripped.slice(2)}` : stripped;
  return LEAD_E164_PATTERN.test(normalized) ? normalized : null;
}

/**
 * Would `leads_normalize_phone` accept this? Defined in terms of
 * `normalizeLeadPhone` so the strip/`00`→`+`/pattern rules have exactly one
 * implementation — the two used to be separate and could have disagreed.
 */
export function isLeadE164(value: string): boolean {
  return normalizeLeadPhone(value) !== null;
}

/**
 * Would `lead_email_is_plausible` accept this? DEV-820.
 *
 * A CHARACTER-FOR-CHARACTER MIRROR of the SQL in 20260909140000, and it must
 * stay one:
 *
 *     ^[^@\s]+@[^@\s.]+(\.[^@\s.]+)*\.[A-Za-z]{2,}$
 *
 * WHY NOT `CANDIDATE_EMAIL_PATTERN`, which is what this used to be. That one is
 * deliberately loose — `quest_candidates.phone` and `.email` store whatever a
 * vendor returns, so the candidate surfaces accept `a@b` on purpose and
 * `CandidateEditForm` documents the looseness as intentional. `leads.email` is
 * the opposite: the CSV path has NO client-side field validation to lean on, so
 * the RPC is the only check, and a lead whose email cannot match anyone at claim
 * time is a permanently unclaimable row of a stranger's PII.
 *
 * The gap was not theoretical. `a@b.c` (one-letter TLD) and `trailing@x.com.`
 * (trailing dot) pass the candidate pattern and are refused by the RPC — so
 * `MockStaffingService` reported them `inserted` while production reported
 * `bad_email`. For a feature whose entire deliverable IS the report, a mock that
 * disagrees with the server about an outcome is worse than no mock.
 *
 * SHAPE ONLY, and not to become an RFC 5322 validator — the SQL comment says the
 * same. The job is catching a header row, a phone number or a note that landed
 * in the email column, not adjudicating exotic-but-legal addresses.
 */
export const LEAD_EMAIL_PATTERN = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)*\.[A-Za-z]{2,}$/;

/**
 * Would `lead_email_is_plausible` accept this? Trims first, because
 * `leads_normalize_email` trims before it stores.
 */
export function isLeadEmailPlausible(value: string): boolean {
  return LEAD_EMAIL_PATTERN.test(value.trim());
}

/**
 * The four ways a lead write can be refused, checked in the order the form
 * should report them. Both RPCs (`admin_update_lead`,
 * `admin_create_lead_manual`) refuse the same set, so the edit form and the
 * create form share this rather than each pre-empting a different subset.
 *
 * THREE OF THE FOUR MIRROR A DATABASE RULE; ONE DOES NOT. That asymmetry is
 * load-bearing — an earlier version of this comment claimed all four were
 * enforced by the RPCs, which would invite deleting the email check here as
 * redundant:
 *
 * - `nameRequired`   — DB-enforced. `display_name` is NOT NULL, and a
 *                      placeholder is worse than a refusal
 *                      (`buildProfileFromLead` does
 *                      `lead.display_name || fallback.display_name`, so a truthy
 *                      placeholder beats the claimer's own login name).
 * - `contactRequired`— DB-enforced. `CONSTRAINT leads_has_contact`. Without a
 *                      contact the lead can never be claimed: dead data by
 *                      construction.
 * - `phoneNotE164`   — DB-enforced. `leads_normalize_phone` RAISEs on anything
 *                      that is not canonical E.164; see `LEAD_E164_PATTERN`.
 * - `emailInvalid`   — **CLIENT-ONLY.** Neither RPC validates the SHAPE of an
 *                      email: `leads_normalize_email` only trims and lowercases,
 *                      so `'not an email'` would be stored verbatim and simply
 *                      never match anyone at claim time. `CANDIDATE_EMAIL_PATTERN`
 *                      here is the only thing standing between a typo and a
 *                      permanently unclaimable lead — do not remove it on the
 *                      assumption the database repeats it.
 */
export function validateLeadForm(values: LeadFormValues): LeadFormError | null {
  if (!values.displayName.trim()) return "nameRequired";

  const email = values.email.trim();
  const phone = values.phone.trim();
  if (!email && !phone) return "contactRequired";

  if (email && !CANDIDATE_EMAIL_PATTERN.test(email)) return "emailInvalid";
  if (phone && !isLeadE164(phone)) return "phoneNotE164";

  return null;
}

/**
 * Form state → RPC arguments. Blank becomes `null`, never `""`: an empty string
 * and an absent value must not be two different ways of saying the same thing on
 * the way to the database.
 *
 * Casing and phone formatting are left alone on purpose. `leads_normalize_biu`
 * owns those for every writer, and normalizing here would be a second copy of a
 * rule the claim lookup depends on — the drift 20260907150001 exists to prevent.
 */
export function buildLeadInput(values: LeadFormValues): LeadUpdateInput {
  const blankToNull = (v: string) => v.trim() || null;
  return {
    displayName: values.displayName.trim(),
    email: blankToNull(values.email),
    phone: blankToNull(values.phone),
    locationCity: blankToNull(values.locationCity),
    locationCountry: blankToNull(values.locationCountry),
    employmentTitle: blankToNull(values.employmentTitle),
    // Split ONCE, here — not on every keystroke. A controlled input whose value
    // is `skills.join(", ")` while onChange splits and filters eats the comma as
    // it is typed. Same reasoning as CandidateEditForm's `skillsStr`.
    skills: values.skillsStr
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    notes: blankToNull(values.notes),
  };
}

/** Form state seeded from a row, for the edit form and its cancel/restore. */
export function leadFormValues(lead: AdminLead): LeadFormValues {
  return {
    displayName: lead.displayName ?? "",
    email: lead.email ?? "",
    phone: lead.phone ?? "",
    locationCity: lead.locationCity ?? "",
    locationCountry: lead.locationCountry ?? "",
    employmentTitle: lead.employmentTitle ?? "",
    skillsStr: (lead.skills ?? []).join(", "),
    notes: lead.notes ?? "",
  };
}

/**
 * DEV-819 — a blank form, for creating a lead by hand.
 *
 * The create-form counterpart to `leadFormValues`: a manual lead is typed from
 * scratch, so there is no row to seed from and nothing may be pre-filled. A
 * placeholder in any field here would be saved as a fact about a real person and
 * then copied into their profile at claim time.
 *
 * A FUNCTION, NOT A FROZEN CONSTANT. The form holds this in state and replaces
 * it through `setValues`, and `close()` calls this again to discard a draft — a
 * shared object would leak one abandoned draft into the next "New lead" click.
 */
export function emptyLeadFormValues(): LeadFormValues {
  return {
    displayName: "",
    email: "",
    phone: "",
    locationCity: "",
    locationCountry: "",
    employmentTitle: "",
    skillsStr: "",
    notes: "",
  };
}
